import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@dealstack/frontend/shared/components/Alert/Alert';
import { DragAndDrop } from '@dealstack/frontend/shared/components/DragAndDrop/DragAndDrop';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { Modal, ModalContent } from '@dealstack/frontend/shared/components/fundament/Modal/Modal';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { Box, Text, Title } from '@mantine/core';
import { flexRender } from '@tanstack/react-table';
import { memo } from 'react';
import styled from 'styled-components';

import { useCreateInvestmentController } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/CreateInvestment/useCreateInvestmentController';
import { EXCEL_SPREADSHEET_TYPE } from '@dealstack/shared/lib/common/business/excel';
import { CURRENT_VISIBLE_MODAL, useInvestmentsController } from './useInvestmentsController';

interface Props {
	mipId: string;
}

const Investments = ({ mipId }: Props) => {
	const investmentsController = useInvestmentsController(mipId);
	const { tableContainerRef, tanstackTable, rowVirtualizer, rows } = investmentsController;
	const createInvestmentController = useCreateInvestmentController(mipId);
	const totalSize = rowVirtualizer.totalSize;
	const virtualRows = rowVirtualizer.virtualItems;
	const table = tanstackTable;

	const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
	const paddingBottom =
		virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

	return (
		<Box pos="relative">
			<ButtonV2
				onClick={investmentsController.onRefreshTable}
				size="s"
				variant="tertiary"
				buttonTheme="light"
			>
				Refresh
			</ButtonV2>
			<SectionWrapper>
				<BulkUploadSection>
					<Text display="inline" size="B5Medium">
						Edit InvestmentWorkflow:
					</Text>
					<ButtonV2
						size="s"
						variant="primary"
						buttonTheme="light"
						onClick={investmentsController.onDownloadXLSX}
					>
						Download XLSX
					</ButtonV2>

					<BulkUploadEditWrapper>
						<ButtonV2
							onClick={() =>
								investmentsController.setCurrentlyOpenModal(
									CURRENT_VISIBLE_MODAL.BULK_EDIT
								)
							}
							size="s"
							variant="primary"
							buttonTheme="light"
						>
							Bulk edit
						</ButtonV2>
						{investmentsController.currentlyOpenModal ===
							CURRENT_VISIBLE_MODAL.BULK_EDIT && (
							<Modal
								onClose={() =>
									investmentsController.setCurrentlyOpenModal(
										CURRENT_VISIBLE_MODAL.NONE
									)
								}
							>
								<ModalContent>
									<Alert icon="information-filled" state="warning" size="m">
										<AlertTitle>WARNING</AlertTitle>
										<AlertDescription>
											You should only change cost and count columns. If you
											touched other columns, bad things may happen.
										</AlertDescription>
									</Alert>
									<br />
									<DragAndDrop
										uploadFileRequest={investmentsController.bulkEditUpload}
										accept={{
											[EXCEL_SPREADSHEET_TYPE]: ['.xlsx'],
										}}
										invalidTypeMessage="Error"
									/>
								</ModalContent>
							</Modal>
						)}

						{investmentsController.currentlyOpenModal ===
							CURRENT_VISIBLE_MODAL.BULK_EDIT_WARNING && (
							<Modal
								onClose={() =>
									investmentsController.setCurrentlyOpenModal(
										CURRENT_VISIBLE_MODAL.NONE
									)
								}
							>
								<ModalContent>
									<Title order={4} fw={600} mb="1rem">
										Skipped transactions update
									</Title>
									<Alert icon="information-filled" state="warning" size="s">
										<AlertDescription>
											Some of the associated CAP table transactions did not
											update. Please verify them manually to make the
											necessary adjustments
										</AlertDescription>
									</Alert>
									<br />
									{investmentsController.renderBulkEditAlert()}
								</ModalContent>
							</Modal>
						)}
					</BulkUploadEditWrapper>
				</BulkUploadSection>

				<BulkCreateSection>
					<Text display="inline" size="B5Medium">
						Create InvestmentWorkflow:
					</Text>
					<ButtonV2
						size="s"
						variant="primary"
						buttonTheme="light"
						onClick={createInvestmentController.onDownloadXLSXTemplate}
					>
						Download XLSX template
					</ButtonV2>
					<BulkUploadEditWrapper>
						<ButtonV2
							onClick={() =>
								investmentsController.setCurrentlyOpenModal(
									CURRENT_VISIBLE_MODAL.BULK_CREATE
								)
							}
							size="s"
							variant="primary"
							buttonTheme="light"
						>
							Bulk create
						</ButtonV2>
						{investmentsController.currentlyOpenModal ===
							CURRENT_VISIBLE_MODAL.BULK_CREATE && (
							<Modal
								onClose={() =>
									investmentsController.setCurrentlyOpenModal(
										CURRENT_VISIBLE_MODAL.NONE
									)
								}
							>
								<ModalContent>
									<Alert icon="information-filled" state="warning" size="m">
										<AlertTitle>WARNING</AlertTitle>
										<AlertDescription>
											You can create new InvestmentWorkflow by structuring
											data into columns
										</AlertDescription>
									</Alert>
									<br />
									<DragAndDrop
										uploadFileRequest={
											createInvestmentController.bulkCreateUpload
										}
										accept={{
											[EXCEL_SPREADSHEET_TYPE]: ['.xlsx'],
										}}
										invalidTypeMessage="Error"
									/>
								</ModalContent>
							</Modal>
						)}
					</BulkUploadEditWrapper>
					{createInvestmentController.renderBulkCreateAlert()}
				</BulkCreateSection>
			</SectionWrapper>
			<TableWrapper>
				{investmentsController.getMipInvestmentsDataRequest.isLoading ? (
					<InlineLoader />
				) : null}
				<div ref={tableContainerRef} className="table_container">
					<table className="table">
						<thead>
							{table.getHeaderGroups().map((headerGroup) => (
								<tr key={headerGroup.id}>
									{headerGroup.headers.map((header) => {
										return (
											<th
												key={header.id}
												colSpan={header.colSpan}
												style={{ width: header.getSize() }}
											>
												{header.isPlaceholder ? null : (
													<div
														{...{
															className: header.column.getCanSort()
																? 'cursor-pointer select-none'
																: '',
															onClick:
																header.column.getToggleSortingHandler(),
														}}
													>
														{flexRender(
															header.column.columnDef.header,
															header.getContext()
														)}
														{{
															asc: ' 🔼',
															desc: ' 🔽',
														}[header.column.getIsSorted() as string] ??
															null}
													</div>
												)}
											</th>
										);
									})}
								</tr>
							))}
						</thead>
						<tbody>
							{paddingTop > 0 && (
								<tr>
									<td style={{ height: `${paddingTop}px` }} />
								</tr>
							)}
							{virtualRows.map((virtualRow) => {
								const row = rows[virtualRow.index];
								if (!row) {
									return;
								}
								return (
									<tr key={row.id}>
										{row.getVisibleCells().map((cell) => {
											return (
												<td key={cell.id}>
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext()
													)}
												</td>
											);
										})}
									</tr>
								);
							})}
							{paddingBottom > 0 && (
								<tr>
									<td style={{ height: `${paddingBottom}px` }} />
								</tr>
							)}
						</tbody>
					</table>
				</div>
				<br />
				<div>{tanstackTable.getRowModel().rows.length} Rows</div>
			</TableWrapper>
		</Box>
	);
};

const TableWrapper = styled.div`
	border: 1px solid lightgray;

	table {
		border-collapse: collapse;
		border-spacing: 0;
		font-family: arial, sans-serif;
		table-layout: fixed;
		width: 100%;
	}

	thead {
		background: white !important;
		margin: 0;
		position: sticky;
		top: 0;
		opacity: 1;
		z-index: 1;
	}

	th {
		border-bottom: 1px solid lightgray;
		border-right: 1px solid lightgray;
		padding: 2px 4px;
		text-align: left;
	}

	td {
		padding: 6px;
	}

	.table_container {
		border: 1px solid lightgray;
		height: 40rem;
		overflow: auto;
	}
`;

const SectionWrapper = styled.div`
	margin: 1rem 0 1rem 0;
	display: flex;
	gap: 1rem;
	flex-direction: column;
`;

const BulkUploadSection = styled.section`
	display: flex;
	gap: 1rem;
`;

const BulkCreateSection = styled.section`
	display: flex;
	gap: 1rem;
`;
const BulkUploadEditWrapper = styled.div``;
const _InvestmentsExport = memo(Investments);

export { _InvestmentsExport as Investments };
